/**
 * Spec 070 P6: the live view protocol (echo-quant spec 035) as the browser speaks it over
 * `WS /api/quant/view` (echo-core relay, `echo_core/modules/quant/view_protocol.py`).
 *
 * The names mirror the pydantic models in `contract/quant-view-protocol.json`; the committed
 * contract test (`tests/ui/lib/viewProtocol.test.ts`) fails when a frame name, the state set, the
 * error-code table or a close code drifts. Candle `time` is the bucket CLOSE (epoch seconds), the
 * forming candle's `time` is constant for its bucket and `isNew` is true only for the first tick of
 * a bucket (spec D13; pinned by `tests/fixtures/quant/view-frames.json`).
 */
import type { CandlePoint, IndicatorOverlay, MarketIndicatorSpec, ParamValue, PreviewOverlay } from './types'

export type ViewRate = 1 | 5
/** The window the Manual view asks for (echo-quant's own default: `min(2000, cap)`). */
export const DEFAULT_VIEW_BARS = 2000

export const VIEW_RATES: readonly ViewRate[] = [1, 5]

// ---- Client frames --------------------------------------------------------

export interface AuthFrame {
  type: 'auth'
  api_key: string
}
export interface OpenMarket {
  type: 'open'
  kind: 'market'
  instrument: string
  tf: string
  bars?: number | null
  rate_s?: ViewRate
  indicators?: MarketIndicatorSpec[]
}
export interface OpenStrategy {
  type: 'open'
  kind: 'strategy'
  slug: string
  instrument: string
  tf: string
  bars?: number | null
  rate_s?: ViewRate
  params?: Record<string, ParamValue>
}
export interface IndicatorsFrame {
  type: 'indicators'
  indicators: MarketIndicatorSpec[]
}
export interface RateFrame {
  type: 'rate'
  rate_s: ViewRate
}
export interface PingFrame {
  type: 'ping'
  t?: string
}
export type ViewClientFrame = AuthFrame | OpenMarket | OpenStrategy | IndicatorsFrame | RateFrame | PingFrame

// ---- Server frames --------------------------------------------------------

export const VIEW_STATES = ['opening', 'live', 'closed', 'no_feed', 'redis_down', 'replaying', 'reconnecting', 'closing'] as const
export type ViewState = (typeof VIEW_STATES)[number]

export interface AuthResultFrame {
  type: 'auth.result'
  success: boolean
  error?: string | null
}
export interface OpenedFrame {
  type: 'opened'
  kind: 'market' | 'strategy'
  instrument: string
  tf: string
  bars: number
  rate_s: ViewRate
  catalog_version: number
  view_id: string
}
export interface CandlesFrame {
  type: 'candles'
  seq: number
  /** The number of chunk frames (`0 <= seq < total`), not a bar count; the bar window is `opened.bars`. */
  total: number
  candles: CandlePoint[]
}
/** An overlay frame body: the indicator shape, plus 029's `legend`/`stats`/`rev` on the strategy overlay. */
export type ViewOverlay = IndicatorOverlay & Partial<Pick<PreviewOverlay, 'legend' | 'stats' | 'rev'>>
export interface IndicatorFrame {
  type: 'indicator'
  id: string
  rev: number
  last?: number | null
  overlay?: ViewOverlay | null
  /** True when the indicator was dropped from the list (no overlay then). */
  removed?: boolean | null
}
export interface ProvisionalPoint {
  line: string
  time: number
  value: number
}
export interface ProvisionalEntry {
  last: number | null
  points: ProvisionalPoint[]
}
export interface TickFrame {
  type: 'tick'
  candle: CandlePoint
  isNew: boolean
  /** A tf close: the candle is final. */
  closed?: boolean | null
  /** Line values at the forming bar, by indicator id; never changes closed history. */
  provisional?: Record<string, ProvisionalEntry> | null
}
export interface StatusFrame {
  type: 'status'
  state: ViewState
  detail?: string | null
  rate_s?: ViewRate | null
  data_through?: number | string | null
}
export interface PongFrame {
  type: 'pong'
  latencyMs: number
  t?: string | null
}
export interface ErrorFrame {
  type: 'error'
  code: string
  message: string
  [extra: string]: unknown
}
export type ViewServerFrame =
  | AuthResultFrame
  | OpenedFrame
  | CandlesFrame
  | IndicatorFrame
  | TickFrame
  | StatusFrame
  | PongFrame
  | ErrorFrame

/** TS interface name -> wire `type` (and `kind` for the two open frames). The contract test reads these. */
export const CLIENT_FRAME_NAMES = {
  AuthFrame: 'auth',
  OpenMarket: 'open',
  OpenStrategy: 'open',
  IndicatorsFrame: 'indicators',
  RateFrame: 'rate',
  PingFrame: 'ping',
} as const satisfies Record<string, ViewClientFrame['type']>

/** Wire `type` -> contract definition name. */
export const SERVER_FRAME_NAMES = {
  'auth.result': 'AuthResultFrame',
  opened: 'OpenedFrame',
  candles: 'CandlesFrame',
  indicator: 'IndicatorFrame',
  tick: 'TickFrame',
  status: 'StatusFrame',
  pong: 'PongFrame',
  error: 'ErrorFrame',
} as const satisfies Record<ViewServerFrame['type'], string>

// ---- Errors and closes ----------------------------------------------------

export const VIEW_CLOSE_CODES = { normal: 1000, going_away: 1001, policy: 1008, error: 1011, try_again: 1013 } as const

/** Error code -> the WebSocket close code that follows it. */
export const ERROR_CLOSE_CODES = {
  bridge_down: 1013,
  bridge_unconfigured: 1013,
  data_not_covered: 1008,
  heartbeat_timeout: 1008,
  indicator_limit: 1008,
  instrument_not_live: 1008,
  instrument_not_offered: 1008,
  invalid_indicator_params: 1008,
  invalid_params: 1008,
  invalid_request: 1008,
  preview_unsupported: 1008,
  server_closing: 1001,
  slow_client: 1013,
  strategy_unavailable: 1008,
  unauthorized: 1008,
  unknown_strategy: 1008,
  unsupported_indicator: 1008,
  view_cap_reached: 1013,
  view_failed: 1011,
  view_unavailable: 1013,
  window_too_large: 1008,
} as const
export type ViewErrorCode = keyof typeof ERROR_CLOSE_CODES

/** A later `indicators` refusal answers an error frame and keeps the view open. */
export const NON_CLOSING_ERROR_CODES: readonly ViewErrorCode[] = [
  'indicator_limit',
  'invalid_indicator_params',
  'invalid_request',
  'unsupported_indicator',
]

/** Codes where one user-initiated reopen is offered (never an automatic loop). */
export const RECONNECT_ERROR_CODES: readonly ViewErrorCode[] = ['heartbeat_timeout', 'view_failed', 'slow_client']

export const VIEW_LIMITS = { max_client_frame_bytes: 1_000_000, max_indicators: 8, rate_s: [1, 5], timeframes: ['1m', '5m', '15m', '1h', '4h'] } as const

/** The indicator catalog version this client was written against (`opened.catalog_version`). */
export const SUPPORTED_CATALOG_VERSION = 1

export function isViewErrorCode(code: string): code is ViewErrorCode {
  return Object.prototype.hasOwnProperty.call(ERROR_CLOSE_CODES, code)
}

// ---- Session seam ---------------------------------------------------------

/** Why a view ended or refused, for the hook: a coded error, or a bare close with no error frame. */
export interface ViewFailure {
  /** The error `code`; `connection_lost` when the socket closed without an error frame. */
  code: ViewErrorCode | 'connection_lost'
  message: string
  /** The close code, or `null` when the failure did not close the socket (a refused `indicators` list). */
  closeCode: number | null
  /** The whole error frame (extras like `missing_days`, `max_bars`, `instruments`, `supported`, `limit`). */
  frame: ErrorFrame | null
}

export interface MarketSubscribe {
  instrument: string
  tf: string
  bars: number
  rate_s: ViewRate
  indicators: MarketIndicatorSpec[]
}

export interface MarketSession {
  onFrame(cb: (frame: ViewServerFrame) => void): () => void
  onFailure(cb: (failure: ViewFailure) => void): () => void
  sendIndicators(list: MarketIndicatorSpec[]): void
  /** Sends `rate`; never reopens. */
  setRate(rate: ViewRate): void
  close(): void
}

/** The strategy twin: the overlay arrives as the `indicator` frame with id `strategy`. */
export interface StrategyViewSubscribe {
  slug: string
  instrument: string
  tf: string
  bars: number
  rate_s: ViewRate
  params: Record<string, ParamValue>
}
export type StrategyViewSession = Omit<MarketSession, 'sendIndicators'>
